import { useState, useMemo, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import { radioPhotos, PHOTO_CATEGORIES } from '@/mocks/radioContent';
import type { RadioPhoto } from '@/mocks/radioContent';
import PhotoLightbox from './components/PhotoLightbox';

type ViewMode = 'grid' | 'albums' | 'featured';
type PageState = 'loading' | 'error' | 'empty' | 'ready';

const FAVORITES_KEY = 'rfb_favorites';
const FEATURED_COUNT = 12;

const swalMixin = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
});

function loadFavorites(): number[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function saveFavorites(ids: number[]) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
}

function buildAlbumData(photos: RadioPhoto[]) {
  return PHOTO_CATEGORIES.map((cat) => {
    const albumPhotos = photos.filter((p) => p.category === cat.key);
    return {
      key: cat.key,
      label: cat.label,
      icon: cat.icon,
      coverUrl: albumPhotos[0]?.url || photos[0]?.url || '',
      count: albumPhotos.length,
    };
  }).filter((a) => a.count > 0);
}

export default function PublicPhotos() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [photos, setPhotos] = useState<RadioPhoto[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<number[]>(() => loadFavorites());

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.06;
      if (shouldFail) {
        setPageState('error');
      } else if (radioPhotos.length === 0) {
        setPageState('empty');
      } else {
        setPhotos(radioPhotos);
        setPageState('ready');
      }
    }, 800 + Math.random() * 600);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.04;
      if (shouldFail) {
        setPageState('error');
      } else {
        setPhotos(radioPhotos);
        setPageState('ready');
      }
    }, 500 + Math.random() * 500);
    return () => clearTimeout(timer);
  }, []);

  /* ─── Derived data ─── */
  const albums = useMemo(() => buildAlbumData(photos), [photos]);

  const featuredPhotos = useMemo(
    () => [...photos].sort((a, b) => b.likes - a.likes).slice(0, FEATURED_COUNT),
    [photos],
  );

  /* ─── Favorites ─── */
  const isFavorite = useCallback(
    (id: number) => favoriteIds.includes(id),
    [favoriteIds],
  );

  const handleToggleFavorite = useCallback(
    (id: number) => {
      setFavoriteIds((prev) => {
        const next = prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id];
        saveFavorites(next);
        return next;
      });
    },
    [],
  );

  /* ─── Lightbox ─── */
  const openLightbox = useCallback(
    (photo: RadioPhoto) => {
      const idx = photos.findIndex((p) => p.id === photo.id);
      setLightboxIndex(idx >= 0 ? idx : 0);
    },
    [photos],
  );

  const handleLightboxPrev = useCallback(() => {
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      return prev <= 0 ? photos.length - 1 : prev - 1;
    });
  }, [photos.length]);

  const handleLightboxNext = useCallback(() => {
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      return prev >= photos.length - 1 ? 0 : prev + 1;
    });
  }, [photos.length]);

  /* ─── Share ─── */
  const handleShare = useCallback(
    (photo: RadioPhoto) => {
      const shareUrl = `${window.location.origin}/photos?foto=${photo.id}`;
      if (navigator.share) {
        navigator
          .share({ title: photo.caption, url: shareUrl })
          .catch(() => copyShare(shareUrl, photo.caption));
      } else {
        copyShare(shareUrl, photo.caption);
      }
    },
    [],
  );

  function copyShare(url: string, caption: string) {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        swalMixin.fire({
          toast: true,
          position: 'top-end',
          title: 'Enlace copiado',
          text: 'Compartilo con quien quieras.',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        });
      })
      .catch(() => {
        swalMixin.fire({
          title: 'Compartir foto',
          html: `<p class="text-sm text-foreground-500 mb-3">${escapeHtml(caption)}</p>
                 <input id="share-url" class="w-full px-3 py-2 rounded-lg border border-background-200 text-sm" value="${escapeHtml(url)}" readonly onclick="this.select()" />`,
          icon: 'info',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#6b7280',
        });
      });
  }

  /* ─── Album click → switch to grid filtered ─── */
  const [albumFilter, setAlbumFilter] = useState<string | null>(null);

  const displayPhotos = useMemo(() => {
    if (viewMode === 'featured') return featuredPhotos;
    if (albumFilter) return photos.filter((p) => p.category === albumFilter);
    return photos;
  }, [viewMode, albumFilter, photos, featuredPhotos]);

  /* ─── Loading ─── */
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <LoadingSkeleton />
      </div>
    );
  }

  /* ─── Error ─── */
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">Error cargando fotos</h2>
          <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
            No se pudieron cargar las fotos. Verificá tu conexión e intentá de nuevo.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line text-base"></i>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  /* ─── Empty ─── */
  if (pageState === 'empty') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-image-line text-foreground-300 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">Sin fotos aún</h2>
          <p className="text-sm text-foreground-500 leading-relaxed">
            Todavía no hay fotos publicadas. Volvé pronto para ver las novedades.
          </p>
        </div>
      </div>
    );
  }

  /* ─── Ready ─── */
  const lightboxPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Creative%20abstract%20photography%20gallery%20wall%20display%20with%20warm%20golden%20artistic%20lighting%2C%20various%20framed%20photos%20arranged%20aesthetically%20on%20clean%20white%20gallery%20wall%2C%20modern%20art%20exhibition%20atmosphere%2C%20soft%20elegant%20shadows%2C%20minimal%20and%20sophisticated%20gallery%20interior%20design%20with%20subtle%20textures%2C%20fine%20art%20photography%20exhibition%20space%2C%20museum%20quality%20presentation&width=1600&height=900&seq=photos-hero-bg&orientation=landscape"
          alt="Galería de Fotos"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <i className="ri-image-line text-white text-lg"></i>
            </div>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight">Galería de Fotos</h1>
          <p className="text-white/70 text-sm md:text-base mt-3 max-w-lg leading-relaxed">
            Momentos detrás de la radio, eventos, conciertos y todo lo que hacemos junto a nuestra comunidad.
          </p>
          <div className="flex items-center gap-5 mt-4 text-white/60 text-xs md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-image-fill text-white/80"></i>
              {photos.length} fotos
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-folder-line text-white/80"></i>
              {albums.length} álbumes
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-heart-line text-white/80"></i>
              {favoriteIds.length} favoritas
            </span>
          </div>
        </div>
      </section>

      {/* View Switcher */}
      <section className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Segmented control */}
          <div className="flex items-center bg-background-100 rounded-full p-1">
            {([
              { key: 'grid' as ViewMode, icon: 'ri-layout-grid-line', label: 'Cuadrícula' },
              { key: 'albums' as ViewMode, icon: 'ri-folder-line', label: 'Álbumes' },
              { key: 'featured' as ViewMode, icon: 'ri-star-line', label: 'Destacadas' },
            ]).map((v) => (
              <button
                key={v.key}
                onClick={() => {
                  setViewMode(v.key);
                  setAlbumFilter(null);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  viewMode === v.key
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-400 hover:text-foreground-600'
                }`}
              >
                <i className={`${v.icon} text-sm`}></i>
                <span className="hidden sm:inline">{v.label}</span>
              </button>
            ))}
          </div>

          {/* Album filter chips (only in grid mode) */}
          {viewMode === 'grid' && (
            <div className="flex items-center gap-1.5 flex-wrap justify-center">
              <button
                onClick={() => setAlbumFilter(null)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  albumFilter === null
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-600'
                }`}
              >
                Todas
              </button>
              {albums.map((a) => (
                <button
                  key={a.key}
                  onClick={() => setAlbumFilter(albumFilter === a.key ? null : a.key)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                    albumFilter === a.key
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  <i className={`${a.icon} text-xs`}></i>
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Content Area */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* ─── Grid View ─── */}
        {(viewMode === 'grid' || viewMode === 'featured') && (
          <>
            {displayPhotos.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-image-add-line text-foreground-300 text-xl"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-500">Sin fotos en esta categoría</p>
                <p className="text-xs text-foreground-400 mt-1">Probá con otro filtro o volvé más tarde.</p>
                {albumFilter && (
                  <button
                    onClick={() => setAlbumFilter(null)}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-close-line"></i>
                    Quitar filtro
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {displayPhotos.map((photo) => (
                  <button
                    key={photo.id}
                    onClick={() => openLightbox(photo)}
                    className="group relative rounded-xl overflow-hidden bg-background-100 cursor-pointer aspect-[4/3] w-full"
                  >
                    <img
                      src={photo.url}
                      alt={photo.caption}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                      <p className="text-white text-xs font-semibold leading-snug line-clamp-2">
                        {photo.caption}
                      </p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-white/60 text-[10px]">{photo.date}</span>
                        <div className="flex items-center gap-1 text-white/70 text-[10px]">
                          <i className="ri-heart-fill text-red-400 text-[10px]"></i>
                          {photo.likes}
                        </div>
                      </div>
                    </div>

                    {/* Favorite indicator */}
                    {isFavorite(photo.id) && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                        <i className="ri-heart-fill text-red-400 text-xs"></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─── Albums View ─── */}
        {viewMode === 'albums' && (
          <>
            {albums.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-folder-open-line text-foreground-300 text-xl"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-500">Sin álbumes</p>
                <p className="text-xs text-foreground-400 mt-1">Todavía no hay álbumes organizados.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                {albums.map((album) => (
                  <button
                    key={album.key}
                    onClick={() => {
                      setViewMode('grid');
                      setAlbumFilter(album.key);
                    }}
                    className="group relative rounded-2xl overflow-hidden bg-background-100 cursor-pointer aspect-[4/3] w-full"
                  >
                    <img
                      src={album.coverUrl}
                      alt={album.label}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                          <i className={`${album.icon} text-white text-sm`}></i>
                        </div>
                      </div>
                      <p className="text-white text-base font-bold">{album.label}</p>
                      <p className="text-white/60 text-xs mt-0.5">{album.count} fotos</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && lightboxPhoto && (
        <PhotoLightbox
          photos={photos}
          currentIndex={lightboxIndex}
          isFavorite={isFavorite(lightboxPhoto.id)}
          onClose={() => setLightboxIndex(null)}
          onPrev={handleLightboxPrev}
          onNext={handleLightboxNext}
          onToggleFavorite={() => handleToggleFavorite(lightboxPhoto.id)}
          onShare={() => handleShare(lightboxPhoto)}
        />
      )}
    </div>
  );
}

/* ─── Loading Skeleton ─── */
function LoadingSkeleton() {
  return (
    <>
      {/* Hero skeleton */}
      <div className="w-full h-[300px] md:h-[380px] bg-background-100 animate-pulse"></div>

      {/* Switcher skeleton */}
      <div className="sticky top-0 z-30 bg-background-50/95 border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-center">
          <div className="w-64 h-9 rounded-full bg-background-100 animate-pulse"></div>
        </div>
      </div>

      {/* Grid skeleton */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] rounded-xl bg-background-100 animate-pulse"></div>
          ))}
        </div>
      </div>
    </>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}